import type { FileFormatScope } from '@steerfi/shared';
import { IconWorld } from '@tabler/icons-react';
import type React from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { PartySearchPicker } from './party-search-picker';

interface ScopePickerProps {
  scope: FileFormatScope;
  partyId: string | null;
  onScopeChange: (scope: FileFormatScope) => void;
  onPartySelect: (partyId: string) => void;
  partyError?: string | undefined;
}

const SCOPE_OPTIONS: ReadonlyArray<{ value: FileFormatScope; label: string }> = [
  { value: 'system', label: 'System-wide' },
  { value: 'provider', label: 'Provider' },
  { value: 'client', label: 'Client' },
];

// Mirrors the inner card height of PartySearchPicker so all three scope
// options leave the modal at the same height. PartySearchPicker is
// label + gap-2 + (search row ~52px + fixed options list 192px) = ~252px
// for the bordered card section (h-12rem list + p-2 search row).
const SCOPE_DETAIL_CARD_HEIGHT = 'h-[252px]';

const SystemScopeInfo: React.FC = () => (
  <div className="flex flex-col gap-2">
    <Label className="font-semibold">System-wide format</Label>
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-md border bg-muted/30 px-6 text-center',
        SCOPE_DETAIL_CARD_HEIGHT
      )}
    >
      <IconWorld className="h-8 w-8 text-muted-foreground" aria-hidden />
      <div className="space-y-1">
        <p className="text-sm font-medium">Available to every party</p>
        <p className="text-xs text-muted-foreground">
          System-wide formats are not tied to a provider or client and act as the platform default
          for their file type.
        </p>
      </div>
    </div>
  </div>
);

export const ScopePicker: React.FC<ScopePickerProps> = ({
  scope,
  partyId,
  onScopeChange,
  onPartySelect,
  partyError,
}) => {
  return (
    <div className="flex flex-col gap-4">
      {/*
        Native radiogroup composed of three labelled <input type="radio">
        elements, each visually rendered as a segmented-control button. The
        inputs are `sr-only` (focusable + announced, but visually hidden);
        the parent `<label>` carries the styling and is what the user
        actually clicks. This gives us full WAI-ARIA radiogroup keyboard
        semantics for free -- arrow keys cycle, Home/End jump, Tab moves
        in/out of the group -- which the previous `<button role="radio">`
        emulation silently dropped. The shared `name` is what wires the
        browser's single-selection + arrow-cycling behaviour.
      */}
      <div role="radiogroup" aria-label="Scope" className="grid grid-cols-3 gap-2">
        {SCOPE_OPTIONS.map(({ value, label }) => {
          const selected = scope === value;
          const id = `scope-${value}`;
          return (
            <label
              key={value}
              htmlFor={id}
              className={cn(
                'cursor-pointer rounded-md border px-4 py-2.5 text-center text-sm font-medium transition-colors',
                'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                selected
                  ? 'border-foreground bg-background text-foreground shadow-sm'
                  : 'border-border bg-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <input
                id={id}
                type="radio"
                name="file-format-scope"
                value={value}
                checked={selected}
                onChange={() => onScopeChange(value)}
                className="sr-only"
              />
              {label}
            </label>
          );
        })}
      </div>
      {scope === 'system' ? (
        <SystemScopeInfo />
      ) : (
        // `key={scope}` forces a fresh mount when switching between
        // 'provider' and 'client' so the previous scope's results never
        // flash through under the new label while the next fetch is in
        // flight. PartySearchPicker initializes `isLoading=true`, so the
        // remount goes straight into skeleton rows.
        <PartySearchPicker
          key={scope}
          scope={scope}
          selectedPartyId={partyId}
          onSelect={onPartySelect}
          error={partyError}
        />
      )}
    </div>
  );
};
